import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Check, Copy, Download, QrCode, RefreshCw, ScanLine, Sparkles } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@insolech/ui';
import { patientPublicQueries, readViteEnv, type AdminQrSummaryRow } from '@insolech/sdk';
import { supabase } from '@/lib/supabase';

/**
 * AdminQrSection - NFC token operations.
 *
 * Per-line QR oversight for admins on the order detail page. For every
 * eligible (custom insole / sandal) line it surfaces:
 *   - state badge (active / expired / revoked / not generated)
 *   - the short code (INS-XXXXX) + the SHORT URL, recommended for the NFC tag
 *     (it fits comfortably in an NTAG213 and is derivable from the code forever)
 *   - a QR PNG of the short URL, rendered client-side + downloadable
 *   - copy buttons for the short code and the short URL
 *   - Generate / Regenerate (rotates the token) + Revoke actions
 *
 * The order also gets a section-level "Mint QR tokens" button and a one-shot
 * lazy self-heal on view: if any eligible line is missing an active token
 * (a missed auto-mint, or a historical order), we mint the gap. This is the
 * on-demand path for historical orders (no mass backfill).
 *
 * Security note: the FULL token URL (/p/<token>) is shown ONCE, right after
 * generation, because the raw token is never stored (only its hash). The
 * durable artifact for the physical tag is therefore the short URL.
 */

interface LineIndexEntry {
  id: string;
  label: string;
  /** Custom insole / sandal lines get a tag; marketplace/OTS lines do not. */
  eligible: boolean;
}

interface Props {
  orderId: string;
  /** All order lines with a short label + eligibility, used to name each QR
   *  and to know which lines should carry a tag. */
  linesIndex: LineIndexEntry[];
}

/** Public base for the patient page. The admin app and the patient /q/ route
 *  are the same deployment, so the host the admin is currently on always serves
 *  the QR target — the most reliable base for a durable NFC URL. We deliberately
 *  prefer window.location.origin over VITE_APP_URL, because that env var has
 *  historically pointed at the stale app.insolech.com host (which no longer
 *  resolves); it is used only as a non-browser fallback. */
function appBaseUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }
  const configured = readViteEnv('VITE_APP_URL');
  return (configured ?? '').replace(/\/$/, '');
}

function shortUrlFor(shortCode: string): string {
  return `${appBaseUrl()}/q/${shortCode}`;
}

export function AdminQrSection({ orderId, linesIndex }: Props) {
  const qc = useQueryClient();
  const summary = useQuery({
    ...patientPublicQueries.orderQrSummaryQuery(supabase, orderId),
    enabled: !!orderId,
  });

  const eligibleIds = React.useMemo(
    () => new Set(linesIndex.filter((l) => l.eligible).map((l) => l.id)),
    [linesIndex],
  );

  // One row per eligible line. The admin_order_qr_summary view LEFT JOINs
  // tokens, so a line that has both a revoked and an active token returns two
  // rows; collapse to one, preferring the active token (else the most recent),
  // so keys stay unique and the "missing" count is not inflated by dead tokens.
  const rows = React.useMemo(() => {
    const byLine = new Map<string, AdminQrSummaryRow>();
    for (const r of summary.data ?? []) {
      if (!eligibleIds.has(r.line_id)) continue;
      const prev = byLine.get(r.line_id);
      if (!prev) {
        byLine.set(r.line_id, r);
        continue;
      }
      const better =
        r.qr_state === 'active' && prev.qr_state !== 'active'
          ? r
          : prev.qr_state === 'active' && r.qr_state !== 'active'
            ? prev
            : (r.qr_created_at ?? '') > (prev.qr_created_at ?? '')
              ? r
              : prev;
      byLine.set(r.line_id, better);
    }
    return Array.from(byLine.values());
  }, [summary.data, eligibleIds]);

  const [activeLineId, setActiveLineId] = React.useState<string | null>(null);
  const [issued, setIssued] = React.useState<{
    token: string;
    short_code: string;
    full_url: string;
    short_url: string;
    png_data_url: string | null;
  } | null>(null);

  const invalidateSummary = React.useCallback(() => {
    void qc.invalidateQueries({
      queryKey: patientPublicQueries.patientPublicKeys.qrSummary(orderId),
    });
  }, [qc, orderId]);

  const generate = useMutation({
    mutationFn: (args: { lineId: string; regenerate: boolean }) =>
      patientPublicQueries.generateQrToken(supabase, {
        order_line_id: args.lineId,
        regenerate: args.regenerate,
      }),
    onSuccess: (data) => {
      // A no-op "already active" response carries no raw token to display; just
      // refresh the summary (the persistent per-line QR already shows the short
      // URL). Only a fresh generation opens the one-time full-token panel.
      if (data.already_active) {
        invalidateSummary();
        return;
      }
      setIssued({
        token: data.token ?? '',
        short_code: data.short_code,
        full_url: data.full_url ?? '',
        short_url: data.short_url,
        png_data_url: data.png_data_url,
      });
      invalidateSummary();
    },
  });

  const revoke = useMutation({
    mutationFn: async (qrId: string) => {
      const { error } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('patient_qr_tokens' as any)
        .update({ state: 'revoked' })
        .eq('id', qrId);
      if (error) throw error;
    },
    onSuccess: invalidateSummary,
  });

  const mintAll = useMutation({
    mutationFn: () => patientPublicQueries.mintOrderTokens(supabase, orderId),
    onSuccess: invalidateSummary,
  });

  // A line "needs a tag" when it is neither active (already tagged) nor revoked
  // (deliberately killed — respect it). Covers never-generated and expired.
  const needsTag = React.useCallback(
    (r: AdminQrSummaryRow) => r.qr_state !== 'active' && r.qr_state !== 'revoked',
    [],
  );

  // One-shot lazy self-heal: mint any line missing a tag, once per mount. Arm
  // the guard on the FIRST evaluation regardless of outcome, so a later Revoke
  // (which flips a row off 'active') can never re-trigger a heal and silently
  // undo the revocation.
  const healedRef = React.useRef(false);
  React.useEffect(() => {
    if (healedRef.current || !summary.data) return;
    healedRef.current = true;
    if (rows.some(needsTag)) mintAll.mutate();
    // mintAll is a stable mutation object; rows derives from summary.data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary.data, rows]);

  const missingCount = rows.filter(needsTag).length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="flex items-center gap-2">
          <QrCode className="h-4 w-4" />
          Patient QR codes
        </CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={() => mintAll.mutate()}
          disabled={mintAll.isPending}
          startIcon={mintAll.isPending ? null : <Sparkles className="h-3.5 w-3.5" />}
          data-testid="admin-qr-mint-all"
        >
          {mintAll.isPending
            ? 'Minting…'
            : missingCount > 0
              ? `Mint ${missingCount} QR token${missingCount === 1 ? '' : 's'}`
              : 'Mint QR tokens'}
        </Button>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-xs text-text-muted">
          Each custom line carries its own QR / NFC tag on the packaging. Scanning opens a public
          status + reorder page (no login). The <strong>short URL</strong> below is the recommended
          value to write to the NFC tag: it stays short enough to fit an NTAG213 and never changes.
        </p>

        {summary.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : summary.isError ? (
          <Alert tone="error" title="Could not load QR summary">
            {String(summary.error ?? 'Unknown error')}
          </Alert>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {rows.map((row) => (
              <QrRow
                key={row.line_id}
                row={row}
                lineLabel={
                  linesIndex.find((l) => l.id === row.line_id)?.label ?? row.line_id.slice(0, 8)
                }
                generating={generate.isPending && activeLineId === row.line_id}
                revoking={revoke.isPending && activeLineId === row.line_id}
                busy={mintAll.isPending}
                onGenerate={() => {
                  setActiveLineId(row.line_id);
                  generate.mutate({ lineId: row.line_id, regenerate: !!row.qr_id });
                }}
                onRevoke={() => {
                  if (!row.qr_id) return;
                  setActiveLineId(row.line_id);
                  revoke.mutate(row.qr_id);
                }}
              />
            ))}
            {rows.length === 0 ? (
              <li className="py-3 text-sm text-text-muted">
                No taggable (custom insole / sandal) lines on this order.
              </li>
            ) : null}
          </ul>
        )}

        {generate.isError ? (
          <Alert tone="error" title="Could not generate QR">
            {(generate.error as Error & { code?: string })?.message ?? String(generate.error)}
          </Alert>
        ) : null}
        {revoke.isError ? (
          <Alert tone="error" title="Could not revoke QR">
            {(revoke.error as Error)?.message ?? String(revoke.error)}
          </Alert>
        ) : null}
        {mintAll.isError ? (
          <Alert tone="error" title="Could not mint QR tokens">
            {(mintAll.error as Error)?.message ?? String(mintAll.error)}
          </Alert>
        ) : null}

        {issued ? <IssuedTokenPanel issued={issued} onClose={() => setIssued(null)} /> : null}
      </CardBody>
    </Card>
  );
}

function QrRow({
  row,
  lineLabel,
  generating,
  revoking,
  busy,
  onGenerate,
  onRevoke,
}: {
  row: AdminQrSummaryRow;
  lineLabel: string;
  generating: boolean;
  revoking: boolean;
  /** A batch mint (self-heal / "Mint QR tokens") is in flight — disable per-line
   *  actions so a manual generate can't race the batch mint on the same line. */
  busy: boolean;
  onGenerate: () => void;
  onRevoke: () => void;
}) {
  const isActive = row.qr_state === 'active';
  const stateBadge = row.qr_state ? (
    <Badge variant={isActive ? 'success' : 'muted'}>{row.qr_state}</Badge>
  ) : (
    <Badge variant="muted">not generated</Badge>
  );

  const shortUrl = row.short_code ? shortUrlFor(row.short_code) : null;

  return (
    <li className="py-3 flex flex-wrap items-start gap-4" data-testid={`admin-qr-row-${row.line_id}`}>
      {isActive && shortUrl ? (
        <QrImage text={shortUrl} shortCode={row.short_code!} />
      ) : null}

      <div className="flex-1 min-w-[12rem]">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium">{lineLabel}</p>
          {stateBadge}
        </div>

        {row.short_code ? (
          <div className="mt-2 space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono bg-surface-raised border border-border-subtle px-1.5 py-0.5 rounded">
                {row.short_code}
              </span>
              <CopyButton value={row.short_code} label="Copy code" />
            </div>
            {shortUrl ? (
              <div className="flex items-center gap-2 flex-wrap">
                <code className="text-xs font-mono text-text-secondary break-all">{shortUrl}</code>
                <CopyButton value={shortUrl} label="Copy URL" />
                <Badge variant="muted">for NFC tag</Badge>
              </div>
            ) : null}
          </div>
        ) : null}

        {row.qr_id ? (
          <div className="mt-2 text-xs text-text-secondary flex flex-wrap gap-x-3 gap-y-0.5">
            <span>
              Generated: <strong>{formatDate(row.qr_created_at)}</strong>
            </span>
            <span className="inline-flex items-center gap-1">
              <ScanLine className="h-3 w-3" />
              {row.scan_count ?? 0} scan{(row.scan_count ?? 0) === 1 ? '' : 's'}
              {row.last_scanned_at ? ` (last ${formatDate(row.last_scanned_at)})` : ''}
            </span>
            {(row.reorder_count ?? 0) > 0 ? (
              <span>
                Reorders: <strong>{row.reorder_count}</strong>
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant={row.qr_id ? 'outline' : 'primary'}
            onClick={onGenerate}
            disabled={generating || revoking || busy}
            startIcon={
              generating ? null : row.qr_id ? (
                <RefreshCw className="h-3.5 w-3.5" />
              ) : (
                <QrCode className="h-3.5 w-3.5" />
              )
            }
            data-testid={`admin-qr-action-${row.line_id}`}
          >
            {generating ? 'Generating…' : row.qr_id ? 'Regenerate' : 'Generate'}
          </Button>
          {isActive ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={onRevoke}
              disabled={generating || revoking || busy}
              startIcon={revoking ? null : <Ban className="h-3.5 w-3.5" />}
              data-testid={`admin-qr-revoke-${row.line_id}`}
            >
              {revoking ? 'Revoking…' : 'Revoke'}
            </Button>
          ) : null}
        </div>
      </div>
    </li>
  );
}

/** Renders a QR PNG for arbitrary text, code-split so the qrcode lib only
 *  loads when the admin actually opens an order with active tags. */
function QrImage({ text, shortCode }: { text: string; shortCode: string }) {
  const [dataUrl, setDataUrl] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    setDataUrl(null);
    setFailed(false);
    void (async () => {
      try {
        const QR = await import('qrcode');
        const url = await QR.toDataURL(text, {
          width: 128,
          margin: 1,
          errorCorrectionLevel: 'M',
        });
        if (alive) setDataUrl(url);
      } catch {
        if (alive) setFailed(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [text]);

  if (failed) {
    return (
      <div className="h-32 w-32 flex items-center justify-center rounded border border-border-subtle text-xs text-text-muted">
        QR failed
      </div>
    );
  }
  if (!dataUrl) return <Skeleton className="h-32 w-32" />;
  return (
    <div className="space-y-1">
      <img
        src={dataUrl}
        alt={`QR code for ${text}`}
        className="h-32 w-32 bg-white rounded border border-border-subtle"
      />
      <a
        href={dataUrl}
        download={`insolech-qr-${shortCode}.png`}
        className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700"
      >
        <Download className="h-3 w-3" />
        PNG
      </a>
    </div>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={() => {
        void navigator.clipboard
          .writeText(value)
          .then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          })
          .catch(() => {
            /* clipboard blocked; noop */
          });
      }}
      startIcon={
        copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />
      }
    >
      {copied ? 'Copied' : label}
    </Button>
  );
}

function IssuedTokenPanel({
  issued,
  onClose,
}: {
  issued: {
    token: string;
    short_code: string;
    full_url: string;
    short_url: string;
    png_data_url: string | null;
  };
  onClose: () => void;
}) {
  return (
    <div className="rounded-md border border-success-200 bg-success-50 p-3 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-success-800">QR generated</p>
          <p className="text-xs text-success-700 mt-0.5">
            The full token URL below is shown ONCE (the raw token is never stored). For the physical
            tag, the short URL is the durable form.
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-3 items-start">
        {issued.png_data_url ? (
          <img
            src={issued.png_data_url}
            alt="QR code"
            className="h-32 w-32 bg-white rounded border border-border-subtle"
          />
        ) : null}
        <div className="text-xs space-y-1.5 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-text-muted">Short code:</span>
            <span className="font-mono font-semibold">{issued.short_code}</span>
            <CopyButton value={issued.short_code} label="Copy" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-text-muted">Short URL:</span>
            <code className="font-mono break-all">{issued.short_url}</code>
            <CopyButton value={issued.short_url} label="Copy" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-text-muted">Full URL:</span>
            <code className="font-mono break-all">{issued.full_url}</code>
            <CopyButton value={issued.full_url} label="Copy" />
          </div>
        </div>
      </div>
      {issued.png_data_url ? (
        <a
          href={issued.png_data_url}
          download={`insolech-qr-${issued.short_code}.png`}
          className="inline-flex items-center gap-1.5 text-xs text-brand-600 hover:text-brand-700"
        >
          <Download className="h-3.5 w-3.5" />
          Download PNG
        </a>
      ) : null}
    </div>
  );
}

function formatDate(s: string | null): string {
  if (!s) return '-';
  try {
    return new Date(s).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return s;
  }
}
